import React, { useCallback, useEffect, useState } from 'react';
import { Col, Input, Row, Tabs } from "antd";
import { useTranslation } from "react-i18next";
import { WinterCerealFertilizerDetails } from "../../../../models/chemicalization/winterCerealFertilizer";
import { SelectTerritorialUnits } from "../../../../components/selects";
import { FertilizerUsageCrops } from "../../../../models/chemicalization/fertilizerUsage";

interface IProps {
  setItems: (items: WinterCerealFertilizerDetails[]) => void;
  items: WinterCerealFertilizerDetails[] | null;
}

export const WinterCerealFertilizerItems: React.FC<IProps> = ({ setItems, items }) => {
  const { t } = useTranslation();
  const [ winterCerealFertilizerDetailsItems, setWinterCerealFertilizerDetailsItems ] = React.useState<WinterCerealFertilizerDetails[]>([]);
  const [ activeKey, setActiveKey ] = useState(0);

  // Функция для генерации уникального ключа
  const generateUniqueKey = (): number => {
	return Math.random();
  };

  // Создание нового элемента с уникальным ключом
  const createNewItem = (): WinterCerealFertilizerDetails => ({
	key: generateUniqueKey(),
	id: null,
	territorialUnitDistrictId: null,
	neededPKg: null,
	neededKKg: null,
	totalSownArea: null,
	factSownArea: null,
	fertilizedArea: null
  });

  useEffect(() => {
	if (items && items.length > 0) {
	  const itemsWithKeys = items.map((item) => ({
		...item,
		key: item.key || generateUniqueKey(),
	  }));
	  setWinterCerealFertilizerDetailsItems(itemsWithKeys);
	} else {
	  setWinterCerealFertilizerDetailsItems([createNewItem()]);
	}
  }, []);

  useEffect(() => {
	setItems(winterCerealFertilizerDetailsItems);
  }, [winterCerealFertilizerDetailsItems]);

  const handleAddItem = useCallback(() => {
	setWinterCerealFertilizerDetailsItems((prevItems) => {
	  const newItem = createNewItem();
	  const newItems = [...prevItems, newItem];

	  setActiveKey(newItems.length - 1); // Переключаемся на новый таб

	  return newItems;
	});
  }, []);

  const handleDeleteItem = useCallback(
	(key: number) => {
	  setWinterCerealFertilizerDetailsItems((prevItems) => {
		const newItems = prevItems.filter((_, i) => i !== key);

		if (newItems.length === 0) return newItems;

		const newActiveKey = Math.max(0, key - 1);
		setActiveKey(newActiveKey);

		setItems(newItems);

		return newItems;
	  });
	},
	[]
  );

  const handleInputChange = (key: number, field: keyof WinterCerealFertilizerDetails, value: any) => {
	setWinterCerealFertilizerDetailsItems((prevItems) =>
	  prevItems.map((item) =>
		item.key === key ? { ...item, [field]: value } : item
	  )
	);
	const updatedWinterCerealFertilizerDetails = winterCerealFertilizerDetailsItems.map((item) =>
	  item.key === key ? { ...item, [field]: value } : item
	);
	setWinterCerealFertilizerDetailsItems(updatedWinterCerealFertilizerDetails);
	setItems(updatedWinterCerealFertilizerDetails);
  };

  const isFieldEmpty = (value: any) => {
	return value === null || value === undefined || value === "";
  };

  return (
	<div style={ { marginTop: 15 } }>
	  <Tabs
		type="editable-card"
		activeKey={ activeKey.toString() }
		onChange={ (key) => setActiveKey(Number(key)) }
		onEdit={ (key, action) => {
		  if (action === "add") {
			handleAddItem();
		  } else if (action === "remove") {
			handleDeleteItem(Number(key));
		  }
		} }
	  >
		{ winterCerealFertilizerDetailsItems.map((item, index) => (
		  <Tabs.TabPane
			key={ index.toString() }
			tab={ item.territorialUnitDistrict?.nameRu || t("general.new_district") }
			closable={ winterCerealFertilizerDetailsItems.length > 1 }
		  >
			<div key={ item.id } className="block_item_form massive_blocks">
			  <Row style={ { margin: 0 } } gutter={ 16 }>
				<Col className="gutter-row" span={ 12 }>
				  <label>{ t("titles.territorialUnitDistricts") }</label>
				  <SelectTerritorialUnits
					type={ ["AREA"] }
					value={ item.territorialUnitDistrictId || null }
					status={ isFieldEmpty(item.territorialUnitDistrictId) ? "error" : "" }
					onChange={ (value) =>
					  handleInputChange(item.key as number, "territorialUnitDistrictId", value)
					}
				  />
				</Col>
				<Col className="gutter-row" span={ 12 }>
				  <label>{ t("chemicalization.winter_cereal_fertilizer.totalSownArea") }</label>
				  <Input
					type="number"
					className={ isFieldEmpty(item.totalSownArea) ? "error-border" : "" }

					value={ item.totalSownArea || "" }
					onChange={ (e) =>
					  handleInputChange(item.key as number, "totalSownArea", Number(e.target.value))
					}
				  />
				</Col>
				<div style={ { backgroundColor: "white", borderRadius: "8px", marginTop: "15px", padding: "15px" } }>
				  <Row style={ { margin: 0 } } gutter={ 16 }>
					<Col className="gutter-row" span={ 8 }>
					  <label>{ t("chemicalization.winter_cereal_fertilizer.neededPKg") }</label>
					  <Input
						type="number"
						className={ isFieldEmpty(item.neededPKg) ? "error-border" : "" }

						value={ item.neededPKg || "" }
						onChange={ (e) =>
						  handleInputChange(item.key as number, "neededPKg", Number(e.target.value))
						}
					  />
					</Col>
					<Col className="gutter-row" span={ 8 }>
					  <label>{ t("chemicalization.winter_cereal_fertilizer.neededKKg") }</label>
					  <Input
						type="number"
						className={ isFieldEmpty(item.neededKKg) ? "error-border" : "" }

						value={ item.neededKKg || "" }
						onChange={ (e) =>
						  handleInputChange(item.key as number, "neededKKg", Number(e.target.value))
						}
					  />
					</Col>
					<Col className="gutter-row" span={ 8 }>
					  <label style={ {
						display: "flex",
						justifyContent: "center",
					  } }>{ t("chemicalization.winter_cereal_fertilizer.totalNeededKg") }</label>
					  <Input
						value={(item.neededPKg || 0) + (item.neededKKg || 0)}
						disabled
						placeholder="Всего" />
					</Col>
				  </Row>
				</div>
				<Row style={ { marginTop: 5 } } gutter={ 16 }>
				  <Col className="gutter-row" span={ 12 }>
					<label>{ t("chemicalization.winter_cereal_fertilizer.factSownArea") }</label>
					<Input
					  type="number"
					  className={ isFieldEmpty(item.factSownArea) ? "error-border" : "" }

					  value={ item.factSownArea || "" }
					  onChange={ (e) =>
						handleInputChange(item.key as number, "factSownArea", Number(e.target.value))
					  }
					/>
					<label>{ t("chemicalization.winter_cereal_fertilizer.fertilizedArea") }</label>
					<Input
					  type="number"
					  className={ isFieldEmpty(item.fertilizedArea) ? "error-border" : "" }

					  value={ item.fertilizedArea || "" }
					  onChange={ (e) =>
						handleInputChange(item.key as number, "fertilizedArea", Number(e.target.value))
					  }
					/>
				  </Col>
				  <Col className="gutter-row" span={ 12 } style={ { marginTop: 40 } }>
					<label style={ {
					  display: "flex",
					  justifyContent: "center",
					  marginTop: "20px"
					} }>{ t("Процент удобренной площади (%)") }</label>
					<Input
					  value={item.factSownArea ? (((item.fertilizedArea || 0) * 100) / item.factSownArea).toFixed(2) : "0"}
					  disabled
					  placeholder='% Соотношение'
					/>

				  </Col>
				</Row>
			  </Row>
			</div>
		  </Tabs.TabPane>
		)) }
	  </Tabs>
	</div>
  );
};

